// The Cases list's words for one case, kept apart so they can be tested:
// who owns it, who it waits on and since when, its new copies, when it was
// approved and what a search matched (CMS review nadia-02, nadia-15,
// nadia-20, rania-12).

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const DAY = 24 * 60 * 60 * 1000;

const toDate = (value) => {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

// "29 Oct", with the year when it isn't this year.
export function shortDay(value, now = new Date()) {
  const d = toDate(value);
  if (!d) return "";
  return `${d.getDate()} ${MONTHS[d.getMonth()]}${d.getFullYear() === now.getFullYear() ? "" : ` ${d.getFullYear()}`}`;
}

// Whole calendar days between a date and now.
export function daysSince(value, now = new Date()) {
  const d = toDate(value);
  if (!d) return null;
  const start = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  return Math.max(Math.round((today - start) / DAY), 0);
}

const daysWords = (n) => (n === 0 ? "today" : n === 1 ? "yesterday" : `${n} days ago`);

// The owner's first name, with the family name's initial when two people
// on the list share a first name (nadia-20: an initial alone says nothing).
export function ownerLabel(name, others = []) {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "";
  const first = parts[0].charAt(0).toUpperCase() + parts[0].slice(1);
  const clash = others.some((other) => {
    const o = String(other || "").trim().split(/\s+/).filter(Boolean);
    return o.length && o[0].toLowerCase() === parts[0].toLowerCase() && o.join(" ").toLowerCase() !== parts.join(" ").toLowerCase();
  });
  return clash && parts[1] ? `${first} ${parts[1].charAt(0).toUpperCase()}.` : first;
}

// Who the case waits on, when it is the customer (nadia-20): a new copy
// they were emailed about, or a draft they haven't sent. Null otherwise.
export function customerWait(item, now = new Date()) {
  if (item?.customer_since) {
    const n = Number(item.new_asked) || 1;
    const since = shortDay(item.customer_since, now);
    return { text: `Waiting on the customer · since ${since}`, short: `Customer · since ${since}`, why: n === 1 ? "a new copy" : `${n} new copies` };
  }
  if (item?.status === "incomplete" && item?.submitted_at) {
    const since = shortDay(item.submitted_at, now);
    return { text: `Waiting on the customer · draft since ${since}`, short: `Customer · draft since ${since}`, why: "they haven’t submitted it" };
  }
  return null;
}

// "29 Oct 2026"
export function longDay(value) {
  const d = toDate(value);
  return d ? `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}` : "";
}

// The two new-copy states, in the case screen's words (nadia-15): marked
// for a new copy but not emailed yet, and asked (emailed) and waiting on
// the customer. asked: the day the oldest still waiting was asked for.
export function newCopyWords(count, state, asked) {
  const n = Number(count) || 0;
  if (!n) return "";
  const what = n === 1 ? "New copy" : `${n} new copies`;
  if (state === "needed") return `${what} needed, not sent yet`;
  const day = longDay(asked);
  return `${what} asked${day ? ` on ${day}` : ""}, waiting for customer`;
}

// The new-copy chips of one case. An older API only counts them all, and
// can't say whether they were sent.
export function newCopyChips(item) {
  const all = Number(item?.needs_new) || 0;
  if (!all) return [];
  if (item?.new_asked == null) return [{ key: "new", label: all === 1 ? "New copy needed" : `${all} new copies needed`, tone: "warning" }];
  const asked = Math.min(Number(item.new_asked) || 0, all);
  const notAsked = all - asked;
  const chips = [];
  if (notAsked) chips.push({ key: "not_asked", label: newCopyWords(notAsked, "needed"), tone: "warning" });
  if (asked) chips.push({ key: "asked", label: newCopyWords(asked, "asked", item.customer_since), tone: "default" });
  return chips;
}

// "Approved 3 days ago" for Visa to send (rania-12), or null.
export function approvedAgo(item, now = new Date()) {
  if (item?.status !== "approved" || !item?.approved_at) return null;
  const n = daysSince(item.approved_at, now);
  return n == null ? null : { days: n, text: `Approved ${daysWords(n)}` };
}

// What a search matched when it isn't the account holder (nadia-02),
// with travellers numbered as the case screen numbers them (1611-2):
// "Matched traveller 1611-2, MAYA TESTER (Given names)", the field and its
// value when the name doesn't show it ("… (Passport number RL0123456)"),
// or the traveller's own request number.
export function matchWords(match, caseId) {
  if (!match) return "";
  const name = match.traveller_name || "";
  const number = match.traveller ? (caseId ? `${caseId}-${match.traveller}` : String(match.traveller)) : "";
  const who = number ? `traveller ${number}${name ? `, ${name}` : ""}` : name || "a traveller";
  if (match.kind === "traveller") return `Matched ${who}`;
  if (match.kind === "request") return `Matched ${who} (request ${match.value})`;
  const shown = name && name.toLowerCase().includes(String(match.value || "").toLowerCase());
  return `Matched ${who} (${match.label}${shown ? "" : ` ${match.value}`})`;
}

// "12 Nov" from "2026-11-12" (a day, not an instant), with the year when it
// isn't this year.
function dayOnly(day, now = new Date()) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(day || ""));
  if (!m) return "";
  return `${Number(m[3])} ${MONTHS[Number(m[2]) - 1]}${Number(m[1]) === now.getFullYear() ? "" : ` ${m[1]}`}`;
}

// When a case is due (API shared/helper/caseDue.js; CMS review round 2,
// rania-10): "Due 12 Nov · in 3 days", "Due today", or past it "Late: due
// 30 Oct, 5 days ago". why: where the day comes from. Null when there is
// no day, or the promise was kept.
export function dueWords(due, now = new Date()) {
  if (!due?.due_on || !["running", "late"].includes(due.state)) return null;
  const day = dayOnly(due.due_on, now);
  const n = Number(due.days);
  const why =
    due.source === "promise"
      ? due.kind === "appointment_booked"
        ? "The on-time promise: the appointment booked by this day"
        : "The on-time promise: the visa by this day"
      : "The date the customer was shown when they paid (expected by)";
  if (due.state === "late") {
    const ago = -n === 1 ? "1 day ago" : `${Math.max(-n, 0)} days ago`;
    return { late: true, text: `Late: due ${day}, ${ago}`, short: `Late · due ${day}`, why };
  }
  const when = n === 0 ? "today" : n === 1 ? "tomorrow" : `in ${n} days`;
  return { late: false, soon: n <= 3, text: n <= 1 ? `Due ${when} (${day})` : `Due ${day} · ${when}`, short: n <= 1 ? `Due ${when}` : `Due ${day}`, why };
}

// The name a list shows for a case: the account holder's, else the first
// traveller's from the passport answers (nadia-18), else null.
export function customerLabel(item) {
  const own = String(item?.customer?.name || "").trim();
  if (own) return { name: own, traveller: false };
  const traveller = String(item?.traveller_name || "").trim();
  return traveller ? { name: traveller, traveller: true } : null;
}
